/* Shared catalog primitives — single source of truth for the Seller and Buyer
 * catalog views so both have identical layout, features and visible fields.
 *
 * Exports:
 *   <CatalogLayoutShell>      search row + category pills + active filters +
 *                             sidebar + main (view toggle + count + items)
 *   <CatalogProductCard mode="seller|buyer" view="grid|list">  unified card/row
 *   <CatalogTextTable mode>   unified text/table view
 *   BUYER_TESTIDS / SELLER_TESTIDS  data-testid maps (parity with old pages)
 *   BADGE_META, FULFILL_CHIP, toggleSet, removeFromSet  shared constants/helpers
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { money } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Search, Package, Building2, Truck, Boxes, X, Check, Sparkles,
  LayoutGrid, Rows3, AlignJustify, Layers, ShieldAlert, ArrowRight,
  Pencil, Trash2, Minus, Plus, MessageSquare,
} from "lucide-react";

// ─────────────────────────── Constants ────────────────────────────────
export const BADGE_META = {
  top_seller:      { label: "Top Seller",      cls: "bg-amber-100 text-amber-800 border-amber-200" },
  new_arrival:     { label: "New",             cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  weekly_special:  { label: "Weekly Special",  cls: "bg-rose-100 text-rose-800 border-rose-200" },
  todays_special:  { label: "Today's Special", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};
export const BADGE_LABEL = {
  new_arrival: "New arrivals",
  top_seller: "Top sellers",
  weekly_special: "Weekly special",
  todays_special: "Today's special",
};
export const FULFILL_CHIP = {
  wholesale: "Wholesale",
  dropship:  "Dropship",
  both:      "Wholesale + Dropship",
};

const AVAILABILITY = [
  { k: "new_arrival",    l: "New arrivals" },
  { k: "top_seller",     l: "Top sellers" },
  { k: "weekly_special", l: "Weekly special" },
  { k: "todays_special", l: "Today's special" },
];
const FULFILL_OPTS = [
  { k: "all", l: "All", icon: Boxes },
  { k: "wholesale", l: "Wholesale", icon: Package },
  { k: "dropship", l: "Dropship", icon: Truck },
  { k: "both", l: "Wholesale + Dropship", icon: Sparkles },
];
const QUICK_PILLS = [
  { k: "all", l: "All" },
  { k: "new_arrival", l: "New arrivals" },
  { k: "top_seller", l: "Top sellers" },
  { k: "weekly_special", l: "Weekly special" },
  { k: "todays_special", l: "Today's special" },
];

export const BUYER_TESTIDS = {
  search: "cat-search", quickPills: "quick-pills", badge: (k) => `badge-${k}`,
  topCats: "top-cats", topCat: (k) => (k === "all" ? "top-cat-all" : `top-cat-${k}`),
  activeFilters: "active-filters", sidebar: "filter-sidebar",
  fulfill: (k) => `fulfill-${k}`, avail: (k) => `avail-${k}`,
  priceSlider: "price-slider", moqSlider: "moq-slider", clearFilters: "clear-filters",
  viewToggle: "view-mode-toggle", view: (k) => `view-${k}`,
  grid: "product-grid", list: "product-list", textTable: "product-text-table",
};
export const SELLER_TESTIDS = {
  search: "seller-cat-search", quickPills: "seller-quick-pills", badge: (k) => `seller-badge-${k}`,
  topCats: "seller-top-cats", topCat: (k) => (k === "all" ? "seller-top-cat-all" : `seller-top-cat-${k}`),
  activeFilters: "seller-active-filters", sidebar: "seller-filter-sidebar",
  fulfill: (k) => `seller-fulfill-${k}`, avail: (k) => `seller-avail-${k}`,
  priceSlider: "seller-price-slider", moqSlider: "seller-moq-slider", clearFilters: "seller-clear-filters",
  viewToggle: "seller-view-toggle", view: (k) => `seller-view-${k}`,
  grid: "seller-grid", list: "seller-list", textTable: "seller-text-table",
};

// ─────────────────────────── Helpers ──────────────────────────────────
export function toggleSet(prev, key) {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
}
export function removeFromSet(prev, key) {
  const next = new Set(prev);
  next.delete(key);
  return next;
}

// ───────────────── Dynamic attribute facets ───────────────────────────
// Extra product attributes that become sidebar filters + search targets
// ONLY when at least one product in the feed has a value for them. Keeps
// the UI clean for sellers who don't use a given field.
export const ATTR_FACET_DEFS = [
  { key: "sub_category", label: "Sub-category" },
  { key: "product_type", label: "Product type" },
  { key: "size",         label: "Size" },
  { key: "gender",       label: "Gender" },
  { key: "condition",    label: "Condition" },
];
const _EMPTY_SET = new Set();

/* useAttrFacets — centralises facet state + value computation + filtering
   for the dynamic attribute filters. Used identically by the Buyer, Seller
   and public Storefront catalogs so all three behave the same. */
export function useAttrFacets(products) {
  const [sel, setSel] = useState({}); // { attrKey: Set<value> }

  const facets = useMemo(() => {
    return ATTR_FACET_DEFS.map((def) => {
      const m = new Map();
      (products || []).forEach((p) => {
        const raw = p[def.key];
        if (raw === undefined || raw === null) return;
        const v = String(raw).trim();
        if (!v) return;
        m.set(v, (m.get(v) || 0) + 1);
      });
      const values = [...m.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => ({ key, count }));
      return { ...def, values };
    }).filter((f) => f.values.length > 0);
  }, [products]);

  const attrFilters = facets.map((f) => ({
    key: f.key,
    label: f.label,
    values: f.values,
    selected: sel[f.key] || _EMPTY_SET,
    setSelected: (next) => setSel((s) => ({ ...s, [f.key]: next })),
  }));

  const applyAttrFacets = useCallback((list) => {
    let r = list;
    for (const def of ATTR_FACET_DEFS) {
      const s = sel[def.key];
      if (s && s.size > 0) {
        r = r.filter((p) => p[def.key] != null && s.has(String(p[def.key]).trim()));
      }
    }
    return r;
  }, [sel]);

  const anyAttrSelected = Object.values(sel).some((s) => s && s.size > 0);
  const clearAttrFacets = useCallback(() => setSel({}), []);
  // Stable dep key so consumers can put it in useMemo/useEffect deps.
  const attrSelKey = JSON.stringify(
    Object.fromEntries(Object.entries(sel).map(([k, v]) => [k, [...(v || [])]])),
  );

  return { attrFilters, applyAttrFacets, anyAttrSelected, clearAttrFacets, attrSelKey };
}
const initialOf = (name) => ((name || "?").trim().charAt(0).toUpperCase() || "?");

// ───────────────── Image with graceful 404 fallback ───────────────────
export function CatalogImage({ src, alt, name, iconClass = "w-12 h-12", fit = "cover" }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [src]);
  if (src && !errored) {
    return <img src={src} alt={alt} loading="lazy" onError={() => setErrored(true)}
      className={fit === "contain" ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"}/>;
  }
  return (
    <div className="w-full h-full grid place-items-center bg-gradient-to-br from-secondary to-foreground/[0.07]">
      {name
        ? <span className="display text-foreground/35" style={{ fontSize: "1.75rem" }}>{initialOf(name)}</span>
        : <Package className={`${iconClass} text-foreground/20`}/>}
    </div>
  );
}

// ─────────────────────────── Small atoms ──────────────────────────────
export function Filt({ label, children, prominent }) {
  return (
    <div className={prominent ? "rounded-xl border border-border bg-secondary/40 p-3.5" : ""}>
      <div className={prominent
        ? "text-[11.5px] font-semibold uppercase tracking-[0.16em] mb-2.5 text-foreground mono"
        : "text-[10px] uppercase tracking-[0.22em] mb-2.5 text-foreground/55 mono"}>{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
export function FiltCheck({ checked, onClick, c, icon: Icon, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full text-left flex items-center justify-between py-1.5 px-2 rounded-md text-xs transition-colors ${checked ? "bg-foreground/5 text-foreground" : "text-foreground/70 hover:bg-secondary"}`}>
      <span className="inline-flex items-center gap-2 min-w-0">
        <span className={`shrink-0 w-3.5 h-3.5 rounded border grid place-items-center transition-colors ${checked ? "bg-foreground border-foreground" : "bg-white border-border"}`} aria-hidden>
          {checked && <Check className="w-2.5 h-2.5 text-background"/>}
        </span>
        {Icon && <Icon className="w-3 h-3 text-foreground/50"/>}
        <span className="truncate">{children}</span>
      </span>
      {c !== undefined && <span className="mono text-[10px] text-foreground/40 shrink-0">{c}</span>}
    </button>
  );
}
export function CatPill({ active, count, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 h-8 rounded-full text-xs transition-colors ${active ? "bg-foreground text-background" : "bg-white border border-border text-foreground/70 hover:border-foreground/40"}`}>
      <span>{children}</span>
      {count !== undefined && <span className={`mono text-[10px] ${active ? "text-accent" : "text-foreground/40"}`}>{count}</span>}
    </button>
  );
}
export function FulfillBadges({ f, className }) {
  return (
    <div className={`flex flex-wrap gap-1 ${className || ""}`}>
      {(f === "wholesale" || f === "both") && (
        <span className="chip bg-foreground text-background text-[10px]"><Package className="w-2.5 h-2.5"/>Wholesale</span>
      )}
      {(f === "dropship" || f === "both") && (
        <span className="chip bg-secondary border border-border text-[10px]"><Truck className="w-2.5 h-2.5"/>Dropship</span>
      )}
    </div>
  );
}
function Th({ children, w, right, left }) {
  return <th style={{ width: w ? `${w}px` : "auto", textAlign: right ? "right" : (left ? "left" : "left") }}
    className="text-[10px] uppercase tracking-[0.18em] mono font-medium py-2.5 px-3">{children}</th>;
}
function Td({ children, right }) {
  return <td style={{ textAlign: right ? "right" : "left", verticalAlign: "middle" }} className="py-2.5 px-3">{children}</td>;
}

// ─────────────── Unified product card (grid + list) ───────────────────
export function CatalogProductCard({
  p, mode, view = "grid", preview = false,
  vis = null,
  // buyer
  selected, onSelect, qty, setQty, unitMode, setMode, onAdd, onDetails, onNegotiate,
  // seller
  onEdit, onDelete,
}) {
  const meta = p.badge ? BADGE_META[p.badge] : null;
  const margin = p.retail_price ? Math.round((1 - p.price / p.retail_price) * 100) : null;
  const isSeller = mode === "seller";
  // Per-field visibility — when no `vis` map is passed every field shows
  // (backward-compatible with all existing usages).
  const show = (f) => !vis || vis[f] !== false;
  const onCardClick = preview ? () => {} : (isSeller ? onEdit : onDetails);
  const pvCls = preview ? "opacity-60 pointer-events-none" : "";
  const stop = (fn) => (e) => { e.stopPropagation(); if (fn) fn(e); };
  const cardTestid = isSeller
    ? (view === "list" ? `seller-row-${p.sku}` : `seller-prod-${p.sku}`)
    : (view === "list" ? `product-row-${p.sku}` : `product-${p.sku}`);

  // ─── Shared visible meta block (category + tiers + fulfillment) ───
  const MetaRow = (
    <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider text-foreground/55">
      {show("category") && <span>{p.category || "Uncategorised"}</span>}
      {p.tiers && p.tiers.length > 0 && (
        <span className="chip text-[9px] bg-foreground/5 border border-border"><Layers className="w-2.5 h-2.5"/>{p.tiers.length}-tier</span>
      )}
      {view === "list" && show("badge") && meta && <span className={`chip text-[9px] border ${meta.cls}`}>{meta.label}</span>}
      {view === "list" && show("fulfillment") && <FulfillBadges f={p.fulfillment} className="inline-flex"/>}
    </div>
  );

  const PriceBlock = (
    <>
      <div className="display text-xl leading-none">{money(p.price)}</div>
      {show("retail_price") && p.retail_price && (
        <div className="text-[10.5px] text-foreground/55 mt-1">
          Retail <span className="line-through">{money(p.retail_price)}</span>
          {margin && <span className="ml-1 text-emerald-700">· {margin}%</span>}
        </div>
      )}
      {show("map_price") && p.map_price != null && (
        <div className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-1">
          <ShieldAlert className="w-3 h-3"/>MAP {money(p.map_price)}
        </div>
      )}
    </>
  );

  // ─────────────────────────── LIST view ──────────────────────────────
  if (view === "list") {
    return (
      <div role="button" tabIndex={0} onClick={onCardClick}
        onKeyDown={(e) => { if (e.key === "Enter") onCardClick(); }}
        className={`rounded-2xl border border-border bg-white hover:border-foreground/40 transition-colors grid ${isSeller ? "grid-cols-[88px_1fr_auto_auto]" : "grid-cols-[auto_88px_1fr_auto_auto]"} gap-4 items-center p-3 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40`}
        data-testid={cardTestid}>
        {!isSeller && (preview
          ? <div/>
          : <input type="checkbox" checked={!!selected} onChange={(e) => onSelect(e.target.checked)} onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-foreground" data-testid={`row-sel-${p.sku}`}/>
        )}
        <div className="w-[88px] h-[88px] rounded-xl bg-secondary overflow-hidden shrink-0">
          <CatalogImage src={p.image} alt={p.name} name={p.name} iconClass="w-7 h-7"/>
        </div>
        <div className="min-w-0">
          {MetaRow}
          <div className="mt-1 font-medium leading-tight truncate">{p.name}</div>
          <div className="text-xs text-foreground/55 mt-0.5 inline-flex items-center gap-1">
            {p.seller_name && <><Building2 className="w-3 h-3"/>{p.seller_name}<span className="mx-1.5 text-foreground/30">·</span></>}
            <span className="mono text-foreground/50">{p.sku}</span>
          </div>
        </div>
        <div className="text-right">
          {PriceBlock}
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/50 mt-1.5">MOQ {p.moq} · {p.stock} in stock</div>
        </div>
        <div className={`flex items-center gap-2 ${isSeller ? "" : pvCls}`} onClick={(e) => e.stopPropagation()}>
          {isSeller ? (
            <>
              <Button size="sm" variant="outline" onClick={onEdit} className="rounded-full h-8 px-3 text-xs" data-testid={`seller-edit-${p.sku}`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>
              <Button size="sm" variant="outline" onClick={onDelete} className="rounded-full h-8 w-8 p-0 text-rose-600 hover:bg-rose-50" data-testid={`seller-delete-${p.sku}`}><Trash2 className="w-3.5 h-3.5"/></Button>
            </>
          ) : (
            <>
              <div className="inline-flex items-center">
                <button onClick={() => setQty(qty - p.moq)} className="w-8 h-8 rounded-md border border-border hover:bg-secondary grid place-items-center"><Minus className="w-3 h-3"/></button>
                <Input value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                  className="w-14 h-8 mx-1 text-center mono text-sm" data-testid={`row-qty-${p.sku}`}/>
                <button onClick={() => setQty(qty + p.moq)} className="w-8 h-8 rounded-md border border-border hover:bg-secondary grid place-items-center"><Plus className="w-3 h-3"/></button>
              </div>
              {onNegotiate && (
                <Button size="sm" variant="outline" onClick={onNegotiate} data-testid={`row-negotiate-${p.sku}`}
                  className="rounded-full h-8 px-3 text-xs text-foreground/70" title="Request quote"><MessageSquare className="w-3 h-3 mr-1"/>Quote</Button>
              )}
              <Button size="sm" variant="outline" onClick={onDetails} className="rounded-full h-8 px-3 text-xs">Details</Button>
              <Button size="sm" onClick={onAdd} data-testid={`row-add-${p.sku}`}
                className="rounded-full h-8 px-3 bg-foreground text-background hover:bg-foreground/90 text-xs">Add <ArrowRight className="w-3 h-3 ml-1"/></Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────── GRID view ──────────────────────────────
  return (
    <div role="button" tabIndex={0} onClick={onCardClick}
      onKeyDown={(e) => { if (e.key === "Enter") onCardClick(); }}
      className="rounded-2xl border border-border bg-white overflow-hidden hover:border-foreground/40 transition-colors flex flex-col cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
      data-testid={cardTestid}>
      {show("image") && (
      <div className="relative h-36 bg-secondary overflow-hidden flex items-center justify-center p-2">
        <CatalogImage src={p.image} alt={p.name} name={p.name} fit="contain"/>
        {show("badge") && meta && <span className={`absolute top-2 left-2 chip text-[10px] border ${meta.cls}`}>{meta.label}</span>}
        {isSeller
          ? (show("fulfillment") && p.fulfillment && <span className="absolute top-2 right-2 chip text-[9.5px] bg-white/90 border border-border">{FULFILL_CHIP[p.fulfillment] || p.fulfillment}</span>)
          : (!preview && (
            <label onClick={(e) => e.stopPropagation()}
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 backdrop-blur grid place-items-center cursor-pointer hover:bg-white">
              <input type="checkbox" checked={!!selected} onChange={(e) => onSelect(e.target.checked)}
                className="w-3.5 h-3.5 accent-foreground" data-testid={`sel-${p.sku}`}/>
            </label>
          ))}
        {show("stock") && p.stock === 0 && <span className="absolute bottom-2 left-2 chip text-[10px] bg-rose-50 border border-rose-200 text-rose-700">Out of stock</span>}
        {show("stock") && p.stock > 0 && p.stock < 20 && <span className="absolute bottom-2 left-2 chip text-[10px] bg-amber-50 border border-amber-200 text-amber-700">Low stock</span>}
      </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        {MetaRow}
        {!isSeller && show("fulfillment") && <FulfillBadges f={p.fulfillment} className="mt-1.5"/>}
        <h3 className="mt-2 font-medium leading-tight">{p.name}</h3>
        {p.seller_name && (
          <div className="text-xs text-foreground/55 mt-0.5 inline-flex items-center gap-1"><Building2 className="w-3 h-3"/>{p.seller_name}</div>
        )}
        {show("sku") && <div className="text-[11px] text-foreground/45 mt-0.5 mono">{p.sku}</div>}
        <div className="mt-3 flex items-end justify-between">
          <div>{PriceBlock}</div>
          <div className="text-right">
            {show("moq") && <>
              <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">MOQ</div>
              <div className="text-sm">{p.moq}</div>
            </>}
            {show("stock") && <div className="text-[10px] mono text-foreground/55 mt-0.5">{p.stock} in stock</div>}
          </div>
        </div>

        {!isSeller && (
          <>
            <div className={`mt-3 inline-flex rounded-full border border-border p-0.5 self-start ${pvCls}`} onClick={(e) => e.stopPropagation()}>
              <button onClick={stop(() => setMode("unit"))}
                className={`text-[10px] px-2.5 py-0.5 rounded-full ${unitMode === "unit" ? "bg-foreground text-background" : "text-foreground/55"}`}>Unit</button>
              <button onClick={stop(() => setMode("case"))}
                className={`text-[10px] px-2.5 py-0.5 rounded-full ${unitMode === "case" ? "bg-foreground text-background" : "text-foreground/55"}`}>Case ({p.case_size || p.moq * 2})</button>
            </div>
            <div className={`mt-3 flex items-center gap-1.5 ${pvCls}`} onClick={(e) => e.stopPropagation()}>
              <button onClick={stop(() => setQty(qty - p.moq))} className="w-8 h-8 rounded-md border border-border hover:bg-secondary grid place-items-center"><Minus className="w-3 h-3"/></button>
              <Input value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 1)} onClick={(e) => e.stopPropagation()}
                className="w-14 h-8 text-center mono text-sm" data-testid={`qty-${p.sku}`}/>
              <button onClick={stop(() => setQty(qty + p.moq))} className="w-8 h-8 rounded-md border border-border hover:bg-secondary grid place-items-center"><Plus className="w-3 h-3"/></button>
            </div>
          </>
        )}

        <div className={`${isSeller ? "mt-4 pt-3 border-t border-border" : `mt-auto pt-3 ${pvCls}`} flex gap-1.5`} onClick={(e) => e.stopPropagation()}>
          {isSeller ? (
            <>
              <Button size="sm" variant="outline" onClick={stop(onEdit)} className="flex-1 rounded-full h-9 text-xs" data-testid={`seller-edit-${p.sku}`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>
              <Button size="sm" variant="outline" onClick={stop(onDelete)} className="h-9 w-9 rounded-full text-rose-600 hover:bg-rose-50" data-testid={`seller-delete-${p.sku}`}><Trash2 className="w-3.5 h-3.5"/></Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={stop(onDetails)} className="flex-1 rounded-full h-9 text-xs">Details</Button>
              <Button size="sm" onClick={stop(onAdd)} data-testid={`add-${p.sku}`}
                className="flex-1 rounded-full h-9 bg-foreground text-background hover:bg-foreground/90 text-xs">Add <ArrowRight className="w-3 h-3 ml-1"/></Button>
            </>
          )}
        </div>
        {!isSeller && onNegotiate && (
          <button onClick={stop(onNegotiate)} data-testid={`negotiate-${p.sku}`}
            className="mt-2 text-[11px] text-foreground/60 hover:text-foreground inline-flex items-center justify-center gap-1 self-center">
            <MessageSquare className="w-3 h-3"/>Request quote
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────── Unified text/table view ──────────────────────────
export function CatalogTextTable({
  mode, rows, preview = false,
  vis = null,
  // buyer
  selected, onSelect, getQty, setQty, onAdd, onDetails, onNegotiate,
  // seller
  onEdit, onDelete,
}) {
  const isSeller = mode === "seller";
  const pvCls = preview ? "opacity-60 pointer-events-none" : "";
  const testid = isSeller ? "seller-text-table" : "product-text-table";
  // Per-column visibility — mirrors the CatalogProductCard `vis` map so
  // consumers get consistent field-hiding across grid / list / text views.
  const show = (f) => !vis || vis[f] !== false;
  const allChecked = !isSeller && rows.length > 0 && rows.every(r => selected[r.id]);
  const toggleAll = (v) => rows.forEach(r => onSelect(r.id, v));
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-secondary/60 border-b border-border">
            <tr className="text-foreground/55">
              {!isSeller && (
                <Th w="36" left>
                  <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} disabled={preview} className="accent-foreground" data-testid="text-select-all"/>
                </Th>
              )}
              <Th w="110" left>SKU</Th>
              <Th left>Product</Th>
              <Th w="120" left>Category</Th>
              {show("fulfillment") && <Th w="130" left>Fulfillment</Th>}
              <Th w="120" right>{isSeller ? "Price" : "Wholesale"}</Th>
              <Th w="80" right>MOQ</Th>
              <Th w="90" right>Stock</Th>
              {!isSeller && <Th w="150" right>Qty</Th>}
              <Th w="100"></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => {
              const meta = p.badge ? BADGE_META[p.badge] : null;
              const margin = p.retail_price ? Math.round((1 - p.price / p.retail_price) * 100) : null;
              const isLast = i === rows.length - 1;
              const stop = (e) => e.stopPropagation();
              const rowTestid = isSeller ? undefined : `text-row-${p.sku}`;
              return (
                <tr key={p.id} onClick={() => { if (preview) return; isSeller ? onEdit(p) : onDetails(p); }}
                  className="hover:bg-secondary/30 cursor-pointer"
                  style={{ borderBottom: isLast ? "none" : "1px solid var(--border, #e5e7eb)" }}
                  data-testid={rowTestid}>
                  {!isSeller && (
                    <Td>
                      <input type="checkbox" checked={!!selected[p.id]} onChange={(e) => onSelect(p.id, e.target.checked)} onClick={stop} disabled={preview} className="accent-foreground"/>
                    </Td>
                  )}
                  <Td><span className="mono text-foreground/70">{p.sku}</span></Td>
                  <Td>
                    <div className="font-medium leading-tight">{p.name}</div>
                    {(p.seller_name || meta) && (
                      <div className="text-[10.5px] text-foreground/55 mt-0.5 inline-flex items-center gap-1">
                        {p.seller_name && <><Building2 className="w-2.5 h-2.5"/>{p.seller_name}</>}
                        {meta && <span className={`chip text-[9px] border ${meta.cls} ${p.seller_name ? "ml-1.5" : ""}`}>{meta.label}</span>}
                      </div>
                    )}
                  </Td>
                  <Td><span className="text-foreground/70">{p.category}</span></Td>
                  {show("fulfillment") && (
                    <Td>{isSeller
                      ? <span className="chip text-[10px] bg-secondary">{FULFILL_CHIP[p.fulfillment] || p.fulfillment || "—"}</span>
                      : <FulfillBadges f={p.fulfillment}/>}</Td>
                  )}
                  <Td right>
                    <div className="mono font-medium">{money(p.price)}</div>
                    {p.retail_price && (
                      <div className="text-[10px] text-foreground/55 mt-0.5"><span className="line-through">{money(p.retail_price)}</span>{margin && <span className="ml-1 text-emerald-700">· {margin}%</span>}</div>
                    )}
                  </Td>
                  <Td right><span className="mono">{p.moq}</span></Td>
                  <Td right><span className={`mono ${p.stock === 0 ? "text-rose-600" : p.stock < 50 ? "text-amber-600" : ""}`}>{p.stock}</span></Td>
                  {!isSeller && (
                    <Td right>
                      <div className={`inline-flex items-center ${pvCls}`} onClick={stop}>
                        <button onClick={() => setQty(p.id, getQty(p) - p.moq)} className="w-7 h-7 rounded-md border border-border hover:bg-secondary grid place-items-center"><Minus className="w-3 h-3"/></button>
                        <Input value={getQty(p)} onChange={(e) => setQty(p.id, parseInt(e.target.value) || 1)}
                          className="w-14 h-7 mx-1 text-center mono text-xs" data-testid={`text-qty-${p.sku}`}/>
                        <button onClick={() => setQty(p.id, getQty(p) + p.moq)} className="w-7 h-7 rounded-md border border-border hover:bg-secondary grid place-items-center"><Plus className="w-3 h-3"/></button>
                      </div>
                    </Td>
                  )}
                  <Td right>
                    <div className={`inline-flex gap-1 items-center ${isSeller ? "" : pvCls}`} onClick={stop}>
                      {isSeller ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => onEdit(p)} className="rounded-md h-7 px-2 text-[11px]" title="Edit"><Pencil className="w-3 h-3"/></Button>
                          <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} className="rounded-md h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50" title="Delete"><Trash2 className="w-3 h-3"/></Button>
                        </>
                      ) : (
                        <>
                          {onNegotiate && (
                            <button onClick={() => onNegotiate(p)} data-testid={`text-negotiate-${p.sku}`} title="Request quote"
                              className="w-7 h-7 rounded-md border border-border hover:bg-secondary grid place-items-center text-foreground/55"><MessageSquare className="w-3 h-3"/></button>
                          )}
                          <Button size="sm" onClick={(e) => { stop(e); onAdd(p); }} data-testid={`text-add-${p.sku}`}
                            className="rounded-full h-7 px-3 bg-foreground text-background hover:bg-foreground/90 text-[11px]">Add</Button>
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────── Active-filters chip bar ──────────────────────────
function ActiveFiltersBar({ testids, search, setSearch, cats, setCats, sellers, setSellers, brandSet, setBrandSet, badgeSet, setBadgeSet, fulfillment, setFulfillment, maxPrice, setMaxPrice, minMoq, setMinMoq, attrFilters = [], onClearAll }) {
  const chips = [];
  if (search) chips.push({ k: `search:${search}`, l: `Search: "${search}"`, onX: () => setSearch("") });
  [...cats].forEach(c => chips.push({ k: `cat:${c}`, l: c, onX: () => setCats(removeFromSet(cats, c)) }));
  if (sellers) [...sellers].forEach(s => chips.push({ k: `seller:${s}`, l: s, onX: () => setSellers(removeFromSet(sellers, s)) }));
  if (brandSet) [...brandSet].forEach(b => chips.push({ k: `brand:${b}`, l: `Brand: ${b}`, onX: () => setBrandSet(removeFromSet(brandSet, b)) }));
  [...badgeSet].forEach(b => chips.push({ k: `badge:${b}`, l: BADGE_LABEL[b] || b, onX: () => setBadgeSet(removeFromSet(badgeSet, b)) }));
  attrFilters.forEach(f => [...f.selected].forEach(v => chips.push({ k: `${f.key}:${v}`, l: `${f.label}: ${v}`, onX: () => f.setSelected(removeFromSet(f.selected, v)) })));
  if (fulfillment !== "all") chips.push({ k: `f:${fulfillment}`, l: `Fulfillment: ${fulfillment}`, onX: () => setFulfillment("all") });
  if (maxPrice < 500) chips.push({ k: "price", l: `≤ $${maxPrice}/unit`, onX: () => setMaxPrice(500) });
  if (minMoq > 1) chips.push({ k: "moq", l: `MOQ ≥ ${minMoq}`, onX: () => setMinMoq(1) });
  if (chips.length === 0) return null;
  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid={testids.activeFilters}>
      <span className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Active</span>
      {chips.map(c => (
        <span key={c.k} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-secondary border border-border text-[11px]">
          {c.l}
          <button onClick={c.onX} className="ml-0.5 text-foreground/55 hover:text-foreground" aria-label={`Remove ${c.l}`}><X className="w-2.5 h-2.5"/></button>
        </span>
      ))}
      <button onClick={onClearAll} className="text-[11px] text-foreground/55 hover:text-foreground underline-offset-2 hover:underline ml-1">Clear all</button>
    </div>
  );
}

// ─────────────────── The full layout shell ────────────────────────────
export function CatalogLayoutShell({
  testids,
  // search
  search, setSearch, searchField, setSearchField, searchFields, searchPlaceholder,
  // quick pills + sort
  badgeSet, setBadgeSet, sort, setSort, sortOptions,
  // categories
  categories, cats, setCats, totalCount,
  // sidebar
  fulfillment, setFulfillment, maxPrice, setMaxPrice, minMoq, setMinMoq,
  // optional: hide the whole Fulfillment filter block (dropship-only surfaces
  // pre-filter to dropship/both — the picker would be inert / confusing).
  hideFulfillmentFilter = false,
  // optional brand (buyer = supplier filter)
  brands, sellers, setSellers,
  // optional product-level brand filter (seller catalog)
  productBrands, brandSet, setBrandSet,
  // dynamic attribute facets (product_type, size, gender, condition, sub_category)
  attrFilters = [],
  // view + counts
  viewMode, setViewMode, filteredCount,
  mainHeaderExtra,
  children,
}) {
  const anyAttr = attrFilters.some(f => f.selected.size > 0);
  const anyFilter = cats.size > 0 || badgeSet.size > 0 || fulfillment !== "all" || maxPrice < 500 || minMoq > 1 || search || (sellers && sellers.size > 0) || (brandSet && brandSet.size > 0) || anyAttr;
  const clearAll = () => {
    setCats(new Set()); setBadgeSet(new Set()); setFulfillment("all");
    setMaxPrice(500); setMinMoq(1); setSearch("");
    if (setSellers) setSellers(new Set());
    if (setBrandSet) setBrandSet(new Set());
    attrFilters.forEach(f => f.setSelected(new Set()));
  };

  return (
    <>
      {/* Search row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder} data-testid={testids.search} className="pl-9 pr-32 h-11"/>
          <select value={searchField} onChange={(e) => setSearchField(e.target.value)}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-9 rounded-md text-xs px-2 bg-secondary border-0">
            {searchFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5 ml-auto" data-testid={testids.quickPills}>
          {QUICK_PILLS.map(b => {
            const isActive = b.k === "all" ? badgeSet.size === 0 : badgeSet.size === 1 && badgeSet.has(b.k);
            return (
              <button key={b.k} onClick={() => setBadgeSet(b.k === "all" ? new Set() : new Set([b.k]))}
                data-testid={testids.badge(b.k)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${isActive ? "bg-foreground text-background" : "bg-white border border-border text-foreground/70 hover:border-foreground/40"}`}>
                {b.l}
              </button>
            );
          })}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-10 rounded-full border border-border bg-white px-3 text-xs">
          {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Top category pills */}
      {categories.length > 0 && (
        <div className="mt-5 -mx-1 overflow-x-auto whitespace-nowrap pb-1.5" data-testid={testids.topCats}>
          <div className="inline-flex gap-1.5 px-1">
            <CatPill active={cats.size === 0} count={totalCount} onClick={() => setCats(new Set())} testid={testids.topCat("all")}>All categories</CatPill>
            {categories.map(c => {
              const isActive = cats.size === 1 && cats.has(c.key);
              return (
                <CatPill key={c.key} active={isActive} count={c.count}
                  onClick={() => setCats(isActive ? new Set() : new Set([c.key]))} testid={testids.topCat(c.key)}>{c.key}</CatPill>
              );
            })}
          </div>
        </div>
      )}

      <ActiveFiltersBar testids={testids}
        search={search} setSearch={setSearch} cats={cats} setCats={setCats}
        sellers={sellers} setSellers={setSellers} brandSet={brandSet} setBrandSet={setBrandSet} badgeSet={badgeSet} setBadgeSet={setBadgeSet}
        fulfillment={fulfillment} setFulfillment={setFulfillment}
        maxPrice={maxPrice} setMaxPrice={setMaxPrice} minMoq={minMoq} setMinMoq={setMinMoq}
        attrFilters={attrFilters}
        onClearAll={clearAll}/>

      {/* Sidebar + main */}
      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-6">
        <aside className="space-y-7" data-testid={testids.sidebar}>
          <Filt label={`Categories${cats.size > 0 ? ` · ${cats.size}` : ""}`} prominent>
            <FiltCheck checked={cats.size === 0} onClick={() => setCats(new Set())} c={totalCount}>All categories</FiltCheck>
            {categories.map(c => (
              <FiltCheck key={c.key} checked={cats.has(c.key)} onClick={() => setCats(toggleSet(cats, c.key))} c={c.count}>{c.key}</FiltCheck>
            ))}
          </Filt>
          {productBrands && productBrands.length > 0 && setBrandSet && (
            <Filt label={`Brand${brandSet && brandSet.size > 0 ? ` · ${brandSet.size}` : ""}`} prominent>
              <FiltCheck checked={!brandSet || brandSet.size === 0} onClick={() => setBrandSet(new Set())} c={totalCount}>All brands</FiltCheck>
              {productBrands.map(b => (
                <FiltCheck key={b.key} checked={brandSet.has(b.key)} onClick={() => setBrandSet(toggleSet(brandSet, b.key))} c={b.count} testid={`brand-filter-${b.key}`}>{b.key}</FiltCheck>
              ))}
            </Filt>
          )}
          {attrFilters.map(f => (
            <Filt key={f.key} label={`${f.label}${f.selected.size > 0 ? ` · ${f.selected.size}` : ""}`} prominent>
              <FiltCheck checked={f.selected.size === 0} onClick={() => f.setSelected(new Set())} c={totalCount}>All {f.label.toLowerCase()}</FiltCheck>
              {f.values.map(v => (
                <FiltCheck key={v.key} checked={f.selected.has(v.key)} onClick={() => f.setSelected(toggleSet(f.selected, v.key))} c={v.count} testid={`${f.key}-filter-${v.key}`}>{v.key}</FiltCheck>
              ))}
            </Filt>
          ))}
          <Filt label="Availability">
            {AVAILABILITY.map(b => (
              <FiltCheck key={b.k} checked={badgeSet.has(b.k)} onClick={() => setBadgeSet(toggleSet(badgeSet, b.k))} testid={testids.avail(b.k)}>{b.l}</FiltCheck>
            ))}
          </Filt>
          {!hideFulfillmentFilter && (
            <Filt label="Fulfillment">
              {FULFILL_OPTS.map(f => (
                <button key={f.k} onClick={() => setFulfillment(f.k)} data-testid={testids.fulfill(f.k)}
                  className={`w-full text-left flex items-center gap-2.5 py-1.5 px-2 rounded-md text-xs transition-colors ${fulfillment === f.k ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary"}`}>
                  <f.icon className="w-3.5 h-3.5"/>{f.l}
                </button>
              ))}
            </Filt>
          )}
          <Filt label="Wholesale price">
            <input type="range" min="1" max="500" value={maxPrice} onChange={(e) => setMaxPrice(parseInt(e.target.value))} className="w-full accent-foreground" data-testid={testids.priceSlider}/>
            <div className="text-[11px] text-foreground/55 mt-1.5 mono">Up to {money(maxPrice)} per unit</div>
          </Filt>
          <Filt label="Min order qty (MOQ)">
            <input type="range" min="1" max="100" value={minMoq} onChange={(e) => setMinMoq(parseInt(e.target.value))} className="w-full accent-foreground" data-testid={testids.moqSlider}/>
            <div className="text-[11px] text-foreground/55 mt-1.5 mono">{minMoq}+ units</div>
          </Filt>
          {brands && setSellers && (
            <Filt label={`Brand${sellers.size > 0 ? ` · ${sellers.size}` : ` (${brands.length})`}`}>
              <FiltCheck checked={sellers.size === 0} onClick={() => setSellers(new Set())}>All brands</FiltCheck>
              {brands.map(b => (
                <FiltCheck key={b} checked={sellers.has(b)} onClick={() => setSellers(toggleSet(sellers, b))} icon={Building2}>{b}</FiltCheck>
              ))}
            </Filt>
          )}
          {anyFilter && (
            <button onClick={clearAll} data-testid={testids.clearFilters}
              className="w-full text-xs text-foreground/60 hover:text-foreground inline-flex items-center justify-center py-2 border-t border-border">
              <X className="w-3 h-3 mr-1"/>Clear all filters
            </button>
          )}
        </aside>

        <main>
          <div className="flex items-center justify-between mb-4 text-xs gap-3 flex-wrap">
            <div className="text-foreground/60">
              Showing <span className="font-medium text-foreground">{filteredCount}</span> of {totalCount} products
            </div>
            <div className="flex items-center gap-3">
              {mainHeaderExtra}
              <div className="inline-flex rounded-full border border-border bg-white p-0.5" data-testid={testids.viewToggle}>
                {[
                  { k: "grid", l: "Grid view", Icon: LayoutGrid },
                  { k: "list", l: "List view", Icon: Rows3 },
                  { k: "text", l: "Text view", Icon: AlignJustify },
                ].map(v => (
                  <button key={v.k} onClick={() => setViewMode(v.k)} data-testid={testids.view(v.k)} title={v.l} aria-label={v.l} aria-pressed={viewMode === v.k}
                    className={`w-8 h-7 rounded-full grid place-items-center transition-colors ${viewMode === v.k ? "bg-foreground text-background" : "text-foreground/55 hover:text-foreground"}`}>
                    <v.Icon className="w-3.5 h-3.5"/>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {children}
        </main>
      </div>
    </>
  );
}
